import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FilterComponent } from "@client/shared/components";
import { globalStyles, PAGE_KEYS } from "@client/shared/constants";
import { getUserData, getPageConfig, setPageConfig } from "@client/utils";
import UsersView from "./view";
import Service from "./service";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Skeleton from "@mui/material/Skeleton";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

const defaultState = {
  entries: [],
  isLoading: false,
  pageNumber: 1,
  pageSize: 5,
  order: null,
  orderBy: "",
  filters: [],
  rowBeingUpdated: null,
};
let gridHelper = null;
const Users = () => {
  const pageConfig = getPageConfig(PAGE_KEYS.USER_PERMISSION);

  const [state, setState] = useState({
    ...defaultState,
    pageSize: pageConfig?.pageSize || defaultState.pageSize,
  });
  const userData = getUserData();

  const hasPermission = (row = {}) => {
    const { roleAccess } = userData?.user || {};
    return !(
      row.roleName === "Super Admin" ||
      row.roleName === roleAccess?.roleName ||
      !roleAccess?.manageUsers
    );
  };

  const renderSwitch = (row, permissionType) => {
    const isSwitchDisabled =
      (permissionType !== "appAccess" && !row.appAccess) ||
      !hasPermission(row) ||
      (!row.sendBroadcasts &&
        (permissionType === "uploadMedia" ||
          permissionType === "accessMedia" ||
          permissionType === "sendToIndividuals"));
    const isUpdating =
      state.rowBeingUpdated?.id === row.rcRoleId &&
      (state.rowBeingUpdated?.permissionType === "appAccess" ||
        state.rowBeingUpdated?.permissionType === permissionType);
    const tooltipTitle =
      !row.sendBroadcasts &&
      (permissionType === "uploadMedia" ||
        permissionType === "accessMedia" ||
        permissionType === "sendToIndividuals")
        ? "To enable this please enable Send Broadcasts first."
        : "";
    return (
      <FormControlLabel
        sx={{
          ml: 0,
          ...(isSwitchDisabled
            ? globalStyles.cursor.default
            : globalStyles.cursor.pointer),
        }}
        label={
          <Typography
            variant="body2"
            sx={{
              ml: 1,
            }}
          >
            {isUpdating ? (
              <Skeleton width={64} height={24} />
            ) : row[permissionType] ? (
              "Enabled"
            ) : (
              "Disabled"
            )}
          </Typography>
        }
        control={
          isUpdating ? (
            <Skeleton width={24} height={24} />
          ) : (
            <Tooltip title={tooltipTitle} placement="top-start">
              <Box
                sx={{
                  ...globalStyles.cursor.default,
                }}
              >
                <Switch
                  disabled={isSwitchDisabled}
                  checked={row[permissionType]}
                  onChange={(evt) => {
                    const { checked } = evt.target;
                    let payload = {
                      [permissionType]: checked,
                      roleName: row.roleName,
                    };
                    if (permissionType === "appAccess" && !checked) {
                      payload = {
                        ...payload,
                        sendBroadcasts: false,
                        uploadMedia: false,
                        accessMedia: false,
                        sendToIndividuals: false,
                        manageUsers: false,
                        //overrideDefaultCallerId: false,
                      };
                    } else if (
                      permissionType === "sendBroadcasts" &&
                      !checked
                    ) {
                      payload = {
                        ...payload,
                        uploadMedia: false,
                        accessMedia: false,
                        sendToIndividuals: false,
                      };
                    }
                    patchUserPermissions(row.rcRoleId, payload, permissionType);
                  }}
                />
              </Box>
            </Tooltip>
          )
        }
      />
    );
  };

  const fetchEntries = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await Service.get();
    if (error) {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
      return toast.error(
        Array.isArray(error) ? error[0].message : error.message
      );
    }
    setState((prevState) => ({
      ...prevState,
      entries: data?.roles || defaultState.entries,
      isLoading: false,
    }));
  }, []);
  const handleGridReady = (instance) => {
    gridHelper = instance;
  };
  const patchUserPermissions = async (id, payload = {}, permissionType) => {
    setState((prevState) => ({
      ...prevState,
      rowBeingUpdated: {
        id,
        permissionType,
      },
    }));
    const { error } = await Service.patch(id, payload);
    if (error) {
      toast.error(
        Array.isArray(error) ? error[0].message : error.message || error
      );
    } else {
      toast.success("Updated successfully.");
      setState((prevState) => ({
        ...prevState,
        entries: prevState.entries.map((entry) => ({
          ...entry,
          ...(entry.rcRoleId === id ? payload : {}),
        })),
      }));
    }
    setState((prevState) => ({
      ...prevState,
      rowBeingUpdated: null,
    }));
  };

  const handlePageNumberChange = useCallback((pageNumber) => {
    setState((prevState) => ({
      ...prevState,
      pageNumber,
    }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setPageConfig(PAGE_KEYS.USER_PERMISSION, {
      pageSize,
    });
    setState((prevState) => ({
      ...prevState,
      pageSize,
      pageNumber: defaultState.pageNumber,
    }));
  }, []);

  const handleSortChange = useCallback((fieldObj, order) => {
    setState((prevState) => ({
      ...prevState,
      order,
      orderBy: order ? fieldObj.field || fieldObj.fieldName : null,
      selectedRows: defaultState.selectedRows,
    }));
  }, []);

  const handleFilterChange = useCallback((filters = []) => {
    setState((prevState) => ({
      ...prevState,
      filters,
    }));
  }, []);

  const filterConfig = [
    {
      field: "roleName",
      fieldToDisplay: "Role Name",
      operatorType: "string",
    },
  ];

  const columnConfig = [
    {
      id: "roleName",
      field: "roleName",
      label: "ROLE NAME",
      canSort: true,
      noResize: true,
    },
    {
      id: "appAccess",
      field: "appAccess",
      label: "APP ACCESS",
      render: (row) => renderSwitch(row, "appAccess"),
      noResize: true,
    },
    {
      id: "sendBroadcasts",
      field: "sendBroadcasts",
      label: "SEND BROADCASTS",
      render: (row) => renderSwitch(row, "sendBroadcasts"),
      noResize: true,
    },
    {
      id: "uploadMedia",
      field: "uploadMedia",
      label: "UPLOAD MEDIA",
      render: (row) => renderSwitch(row, "uploadMedia"),
      noResize: true,
    },
    {
      id: "accessMedia",
      field: "accessMedia",
      label: "ACCESS MEDIA",
      render: (row) => renderSwitch(row, "accessMedia"),
      noResize: true,
    },
    {
      id: "sendToIndividuals",
      field: "sendToIndividuals",
      label: "SEND TO INDIVIDUALS",
      render: (row) => renderSwitch(row, "sendToIndividuals"),
      noResize: true,
    },
    {
      id: "manageUsers",
      field: "manageUsers",
      label: "MANAGE USERS",
      render: (row) => renderSwitch(row, "manageUsers"),
      noResize: true,
    },
    /*
    {
      id: "overrideDefaultCallerId",
      field: "overrideDefaultCallerId",
      label: "DEFAULT CALLER ID",
      render: (row) => renderSwitch(row, "overrideDefaultCallerId"),
      noResize: true,
    },
    */
    {
      id: "col-action",
      label: "",
      renderHeader: () => (
        <Box
          sx={{
            ...globalStyles.display.flex,
            ...globalStyles.flex.justify.end,
          }}
        >
          <FilterComponent
            filterConfig={filterConfig}
            onFilterApply={handleFilterChange}
            appliedFilters={state.filters}
          />
        </Box>
      ),
    },
  ];

  const sortedEntries = (order = null, orderBy = "", entries = []) => {
    if (!order) return entries;
    return entries.sort((a, b) => {
      let result = 0;
      const valueA = (a[orderBy] || "").toLowerCase();
      const valueB = (b[orderBy] || "").toLowerCase();
      if (valueB < valueA) {
        result = -1;
      }
      if (valueB > valueA) {
        result = 1;
      }
      return order === "ASC" ? -result : result;
    });
  };
  const filterEntries = (filters = [], entries = []) => {
    if (!filters.length) return entries;
    return entries.filter((entry) => {
      return filters.every((filter) => {
        const entryValue = (entry[filter.field] || "").toLowerCase();
        const filterValue = (filter.value || "").toLowerCase();
        let isValid = false;
        switch (filter.type) {
          case "iRegexp":
            isValid = entryValue.includes(filterValue);
            break;
          case "notIRegexp":
            isValid = !entryValue.includes(filterValue);
            break;
          case "iLike":
            isValid = entryValue === filterValue;
            break;
          case "notILike":
            isValid = entryValue !== filterValue;
            break;
        }
        return isValid;
      });
    });
  };

  const filteredEntries = filterEntries(state.filters, [...state.entries]);
  const paginatedEntries = useMemo(() => {
    return sortedEntries(state.order, state.orderBy, filteredEntries).slice(
      (state.pageNumber - 1) * state.pageSize,
      (state.pageNumber - 1) * state.pageSize + state.pageSize
    );
  }, [
    state.entries,
    state.pageNumber,
    state.pageSize,
    state.order,
    state.orderBy,
    state.filters,
  ]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return (
    <UsersView
      entries={paginatedEntries}
      entriesCount={filteredEntries.length}
      columnConfig={columnConfig}
      isLoading={state.isLoading}
      pageNumber={state.pageNumber}
      pageSize={state.pageSize}
      order={state.order}
      orderBy={state.orderBy}
      handlePageNumberChange={handlePageNumberChange}
      handlePageSizeChange={handlePageSizeChange}
      handleSortChange={handleSortChange}
      handleGridReady={handleGridReady}
    />
  );
};

export default Users;
